import React, { useState } from "react";
import {
    Plus,
    Trash2,
    Edit,
    Search,
    X,
    Layers,
    Tags,
} from "lucide-react";

export default function HelpdeskCategorySetup() {

    const CATEGORY_TYPES = [
        "IT Support",
        "HR Support",
        "Finance Support",
        "Facilities",
        "Admin Support",
        "Payroll Support",
        "General Queries"
    ];

    const [categories, setCategories] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");

    const [modal, setModal] = useState({
        open: false,
        isEdit: false,
        data: {
            id: null,
            name: "",
            type: "IT Support",
            subcategories: "",
            active: true
        }
    });

    const openCreateModal = () => {
        setModal({
            open: true,
            isEdit: false,
            data: {
                id: null,
                name: "",
                type: "IT Support",
                subcategories: "",
                active: true
            }
        });
    };

    const openEditModal = (item) => {
        setModal({
            open: true,
            isEdit: true,
            data: item
        });
    };

    const saveCategory = () => {
        const d = modal.data;

        if (!d.name.trim()) return alert("Category name is required.");

        const formattedSubcats = d.subcategories
            .split(",")
            .map((s) => s.trim())
            .filter((s) => s.length > 0);

        const newEntry = { ...d, subcategories: formattedSubcats };

        if (modal.isEdit) {
            setCategories((prev) =>
                prev.map((c) => (c.id === d.id ? newEntry : c))
            );
        } else {
            setCategories((prev) => [
                ...prev,
                { ...newEntry, id: Date.now() }
            ]);
        }

        setModal({ ...modal, open: false });
    };

    const deleteCategory = (id) => {
        if (!window.confirm("Delete this category?")) return;
        setCategories((prev) => prev.filter((c) => c.id !== id));
    };

    const filteredCategories = categories.filter((c) =>
        c.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="p-6 md:p-10 bg-gray-50 min-h-screen font-['Poppins','Roboto',sans-serif]">

            {/* Header */}
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-800">Helpdesk Category Setup</h1>
                    <p className="text-gray-500 text-sm mt-1">
                        Create and manage helpdesk ticket categories & subcategories.
                    </p>
                </div>

                <button
                    onClick={openCreateModal}
                    className="flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg shadow hover:bg-teal-700 transition"
                >
                    <Plus className="mr-2" size={18} /> Add Category
                </button>
            </div>

            {/* Search */}
            <div className="flex items-center bg-white shadow px-3 py-2 rounded-lg w-full md:w-1/3 mb-6">
                <Search size={18} className="text-gray-400 mr-2" />
                <input
                    type="text"
                    placeholder="Search categories..."
                    className="outline-none w-full"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
            </div>

            {/* Category List */}
            <div className="bg-white p-4 rounded-xl shadow border">
                {filteredCategories.length === 0 ? (
                    <p className="text-center text-gray-500 py-10 italic">
                        No categories found. Add a new category to begin.
                    </p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredCategories.map((item) => (
                            <div
                                key={item.id}
                                className="p-5 bg-white rounded-xl border shadow hover:shadow-lg transition"
                            >
                                <Layers className="text-teal-600" size={36} />

                                <h3 className="text-lg font-semibold mt-3">{item.name}</h3>
                                <p className="text-sm text-gray-500">{item.type}</p>

                                {/* Subcategories */}
                                {item.subcategories.length > 0 && (
                                    <div className="mt-3">
                                        <p className="text-sm font-medium text-gray-700 flex items-center">
                                            <Tags className="text-gray-500 mr-2" size={16} />
                                            Subcategories:
                                        </p>
                                        <ul className="text-sm text-gray-500 mt-1 list-disc ml-5">
                                            {item.subcategories.map((sub, idx) => (
                                                <li key={idx}>{sub}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Status */}
                                <p className="mt-3 text-sm">
                                    Status:{" "}
                                    <span
                                        className={`font-semibold ${
                                            item.active ? "text-green-600" : "text-red-600"
                                        }`}
                                    >
                                        {item.active ? "Active" : "Inactive"}
                                    </span>
                                </p>

                                {/* Actions */}
                                <div className="flex justify-between mt-4">
                                    <button
                                        onClick={() => openEditModal(item)}
                                        className="text-indigo-600 hover:text-indigo-800 flex items-center"
                                    >
                                        <Edit size={18} className="mr-1" /> Edit
                                    </button>

                                    <button
                                        onClick={() => deleteCategory(item.id)}
                                        className="text-red-600 hover:text-red-800"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modal */}
            {modal.open && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-6">
                    <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-3xl relative">

                        <button
                            onClick={() => setModal({ ...modal, open: false })}
                            className="absolute right-5 top-5 text-gray-600 hover:text-black"
                        >
                            <X size={24} />
                        </button>

                        <h2 className="text-xl font-semibold mb-4">
                            {modal.isEdit ? "Edit Category" : "Add New Category"}
                        </h2>

                        {/* Form */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                            {/* Name */}
                            <div>
                                <label className="text-sm text-gray-600">Category Name</label>
                                <input
                                    type="text"
                                    className="border rounded p-2 w-full mt-1"
                                    value={modal.data.name}
                                    onChange={(e) =>
                                        setModal({
                                            ...modal,
                                            data: { ...modal.data, name: e.target.value }
                                        })
                                    }
                                />
                            </div>

                            {/* Type */}
                            <div>
                                <label className="text-sm text-gray-600">Category Type</label>
                                <select
                                    className="border rounded p-2 w-full mt-1"
                                    value={modal.data.type}
                                    onChange={(e) =>
                                        setModal({
                                            ...modal,
                                            data: { ...modal.data, type: e.target.value }
                                        })
                                    }
                                >
                                    {CATEGORY_TYPES.map((t) => (
                                        <option key={t}>{t}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Subcategories */}
                            <div className="md:col-span-2">
                                <label className="text-sm text-gray-600">
                                    Subcategories (comma separated)
                                </label>
                                <input
                                    type="text"
                                    className="border rounded p-2 w-full mt-1"
                                    placeholder="Example: Laptop Issue, Software Install, Password Reset"
                                    value={modal.data.subcategories}
                                    onChange={(e) =>
                                        setModal({
                                            ...modal,
                                            data: { ...modal.data, subcategories: e.target.value }
                                        })
                                    }
                                />
                            </div>

                            {/* Status Toggle */}
                            <div>
                                <label className="text-sm text-gray-600">Status</label>
                                <select
                                    className="border rounded p-2 w-full mt-1"
                                    value={modal.data.active}
                                    onChange={(e) =>
                                        setModal({
                                            ...modal,
                                            data: {
                                                ...modal.data,
                                                active: e.target.value === "true"
                                            }
                                        })
                                    }
                                >
                                    <option value="true">Active</option>
                                    <option value="false">Inactive</option>
                                </select>
                            </div>
                        </div>

                        {/* Buttons */}
                        <div className="flex justify-end gap-3 mt-5">
                            <button
                                onClick={() => setModal({ ...modal, open: false })}
                                className="px-4 py-2 bg-gray-200 rounded-lg"
                            >
                                Cancel
                            </button>

                            <button
                                onClick={saveCategory}
                                className="px-4 py-2 bg-teal-600 text-white rounded-lg"
                            >
                                {modal.isEdit ? "Save Changes" : "Add Category"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}
